import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { en } from './en.ts';
import { substitute } from './format.ts';
import { DEFAULT_LOCALE, isLocale } from './types.ts';
import type { Locale, TParams } from './types.ts';
import { zhCN } from './zh-CN.ts';

/**
 * Tenant console-web i18n runtime (forked local from the console-shared
 * `createConsoleI18nContext` factory, epic #897 PR 3 — identical behavior,
 * concrete `Locale` / `I18nKey` types instead of factory generics).
 *
 * Locale persistence uses the `console.locale` localStorage key so the
 * user's language preference survives the fork unchanged.
 */

export type I18nKey = keyof typeof zhCN;

const STORAGE_KEY = 'console.locale';

const DICTIONARIES: Readonly<Record<Locale, Readonly<Record<I18nKey, string>>>> = {
  'zh-CN': zhCN,
  en,
};

interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  readonly t: (key: I18nKey, params?: TParams) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  readonly initialLocale?: Locale;
  readonly children: ReactNode;
}

function readStoredLocale(): Locale | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(next: Locale): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal: React state still carries the locale for this session.
  }
}

export function I18nProvider({ initialLocale, children }: I18nProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (initialLocale !== undefined) {
      return initialLocale;
    }
    return readStoredLocale() ?? DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = locale;
    const dict = DICTIONARIES[locale];
    document.title = dict['meta.title'];
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription !== null) {
      metaDescription.setAttribute('content', dict['meta.description']);
    }
  }, [locale]);

  const t = useMemo(() => {
    const dict = DICTIONARIES[locale];
    return (key: I18nKey, params?: TParams): string => substitute(dict[key], params);
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): (key: I18nKey, params?: TParams) => string {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT must be used inside <I18nProvider>');
  }
  return ctx.t;
}

export function useLocale(): {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
} {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useLocale must be used inside <I18nProvider>');
  }
  return { locale: ctx.locale, setLocale: ctx.setLocale };
}
